/**
 * PO Line Detail Tab - extracted from POLineDetailModal.
 *
 * Renders the main detail view (left column: line fields, right column: changelog).
 */
import React, { useMemo } from 'react';
import { Calendar, Hash, DollarSign } from 'lucide-react';
import type { PurchaseOrderLine } from '../../lib/api';
import { formatExchangeRateValue } from '../../lib/exchangeRateDisplay';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { ReadOnlyField, DocumentLinksField } from './POLineDetailFields';
import { POLineDateFields } from './POLineDateFields';
import ContractPriceDetailModal from './ContractPriceDetailModal';
import { collapseChangeRequestRows } from './auditHistoryCollapse';
import {
  getContractPriceVariance, hasContract, isPositive, varianceTextClass,
  NO_CONTRACT_TEXT, LOT_NOT_APPLICABLE_TEXT, UNIT_NOT_APPLICABLE_TEXT,
} from './contractPriceVariance';
import { CUSTOMER_REQUIRED_DATE, SUPPLIER_PROMISE_DATE_SHORT } from '../../config/dateLabels';
import { canonicalChangeField } from './changelogFields';

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

/** Customer review 11 Sep (item 27b): what each `exchange_rate_source` means to a buyer. */
const EXCHANGE_RATE_SOURCE_LABELS: Record<string, string> = {
  po_conversion: 'rate used to convert this order',
  erp_db_rate: 'ERP rate behind the converted values on the dashboards',
  erp_order_rate: 'ERP rate at order time',
};

// EXPEDITING-PARITY PR 2 (Aaron, 17 Sep 2026): a FOURTH copy of the status map
// lived here, exported and imported by nobody — it disagreed with the grid's on
// `wip` and carried no `closed`. Removed rather than marked dormant: the whole
// point of the ruling is that one function answers "what is this line's status",
// and a spare copy of the table is the drift itself, not a feature being
// retired. The answer is `./lineStatus`.

// CR-3403: Interface for pending change
export interface PendingChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  reason?: string;
  changeDate: string;
  changedBy: string;
  icon: React.ReactNode;
}

/** Get pending changes for review based on acknowledge_status. */
export function getPendingChanges(
  line: PurchaseOrderLine,
  changelogs: ChangelogEntry[],
  canShowWorkflow: boolean,
  userPlatform?: string,
  currentUserId?: number,
): PendingChange[] {
  if (!canShowWorkflow) return [];

  const pendingChanges: PendingChange[] = [];
  const status = line.acknowledge_status;

  // CR-4688: If acknowledge_status is no longer change_proposed_*, all
  // proposals have been fully processed — no pending changes to show.
  if (status !== 'change_proposed_supplier' && status !== 'change_proposed_customer') return [];

  const recentChanges = changelogs.filter(log => {
    const changeDate = new Date(log.change_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return changeDate >= thirtyDaysAgo;
  });

  const latestByField: Record<string, ChangelogEntry> = {};
  for (const log of recentChanges) {
    // 'delivery_date' rows (pre-15 Sep 2026) and 'customer_required_date' rows are one field.
    const field = canonicalChangeField(log.field_changed);
    if (!latestByField[field] || new Date(log.change_date) > new Date(latestByField[field].change_date)) {
      latestByField[field] = log;
    }
  }

  const fieldConfig: Record<string, { label: string; icon: React.ReactNode }> = {
    customer_required_date: { label: CUSTOMER_REQUIRED_DATE, icon: <Calendar className="h-5 w-5" /> },
    supplier_promise_date: { label: SUPPLIER_PROMISE_DATE_SHORT, icon: <Calendar className="h-5 w-5" /> },
    quantity: { label: 'Quantity', icon: <Hash className="h-5 w-5" /> },
    unit_price: { label: 'Unit Price', icon: <DollarSign className="h-5 w-5" /> },
  };

  for (const [field, config] of Object.entries(fieldConfig)) {
    const log = latestByField[field];
    if (log && log.old_value && log.new_value) {
      const isProposedChange = log.reason?.toLowerCase().includes('proposed') ||
        status === 'change_proposed_supplier' ||
        status === 'change_proposed_customer';

      if (isProposedChange) {
        // Only show changes from the OTHER party
        // 1. Skip if changed_by is the current user (you don't accept your own proposals)
        if (currentUserId && Number(log.changed_by) === currentUserId) continue;
        // 2. Check company_type on the changelog entry
        const changeCompanyType = (log as unknown as Record<string, unknown>)['company_type'] as string | undefined;
        const isCustomer = userPlatform === 'customer' || userPlatform === 'CUSTOMER' || userPlatform === 'HELIX';
        if (isCustomer && changeCompanyType === 'customer') continue;
        if (!isCustomer && changeCompanyType === 'supplier') continue;

        pendingChanges.push({
          field,
          label: config.label,
          oldValue: log.old_value,
          newValue: log.new_value,
          reason: log.reason,
          changeDate: log.change_date,
          changedBy: log.changed_by,
          icon: config.icon,
        });
      }
    }
  }

  return pendingChanges;
}

/** Get proposer name based on status. */
export function getProposerInfo(line: PurchaseOrderLine) {
  const status = line.acknowledge_status;
  if (status === 'change_proposed_supplier') {
    return { name: line.supplier_name || 'Supplier', type: 'supplier' };
  } else if (status === 'change_proposed_customer') {
    return { name: line.customer_name || 'Customer', type: 'customer' };
  }
  return { name: 'System', type: 'system' };
}

/* ------------------------------------------------------------------ */
/*  Detail Tab Component                                               */
/* ------------------------------------------------------------------ */

interface POLineDetailTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'customer' | 'supplier';
  userId?: number;
  /** Batch 4: a PLAIN 2dp number — the Currency field carries the code. */
  formatCurrency: (value: string) => string;
  formatDate: (dateString: string) => string;
  /** CR-7262: quantities keep up to 3 decimals; see PreferencesContext. */
  formatQuantity: (value: number | string | null | undefined) => string;
  getChangeCount: (fieldName: string) => number | null;
  canShowWorkflowActions: boolean;
  /** CR-4705: Callback to navigate to change requests tab for review. */
  onReviewChanges?: () => void;
  /** CR-7275: schedule-line customer; a lot-priced line then takes no colour. */
  usesScheduleLines?: boolean;
}

export function POLineDetailTabContent({
  line,
  changelogs,
  userType,
  formatCurrency,
  formatDate,
  formatQuantity,
  getChangeCount,
  usesScheduleLines = false,
}: POLineDetailTabProps) {
  // CR-7266: the contract this line's prices resolved against, opened from the
  // Contract field below. Held here rather than lifted to POLineDetailModal:
  // the field is the only thing that opens it, and the surrounding modal
  // already stacks its own dialogs this way.
  const [contractModalId, setContractModalId] = React.useState<number | null>(null);
  // CR-7275: one verdict for the price box the part uses (picked by the
  // verdict's own isPositive), Contract Price and PPV; the unused box says so.
  const onContract = hasContract(line);
  const varianceClass = varianceTextClass(getContractPriceVariance(line, usesScheduleLines));
  const contractUnit = isPositive(line.contract_unit_price) ? line.contract_unit_price : undefined;
  const contractLot = !contractUnit && isPositive(line.contract_lot_price)
    ? line.contract_lot_price : undefined;
  const unitPriced = !!contractUnit;
  const lotPriced = !!contractLot;
  const unitPriceDisplay = !onContract
    ? NO_CONTRACT_TEXT
    : contractUnit
      ? formatCurrency(contractUnit)
      : lotPriced ? UNIT_NOT_APPLICABLE_TEXT : '';
  const lotPriceDisplay = !onContract
    ? NO_CONTRACT_TEXT
    : contractLot
      ? formatCurrency(contractLot)
      : LOT_NOT_APPLICABLE_TEXT;
  // CR-4711 layout:
  //   Row A (header parties): Customer | Supplier Group | Supplier (customer view)
  //                           Customer (supplier view)
  //   Row C: Order Date | Buyer ID                                      (customer-only)
  //   Row D (parts):  Part Number | Part Description | Drawing Issue†
  //   Row D2 (CR-7308 rev 2): Order Description, full width — the line's own
  //                           order text, which runs to several lines
  //   Row E: Batch/Serial No | PnR Note No                              (customer-only)
  //   Row F (qtys):   Qty Ordered | Qty Delivered† | Qty Rejected†
  //                   Qty Accepted† | Qty Outstanding†    (CR-7468)
  //   Row G (dates):  MRP/ERP Scheduled Date | Original Required Date (ODD) | Latest Supplier Promise Date
  //   Row H (price):  Unit Price | Currency† | Exchange Rate†
  //   Row I: Total Value | O/S Value                                    (customer-only)
  //   Row J: Contract Unit Price | Contract Lot Price | Contract Price | PPV  (customer-only)
  //   Row K: PDF Attached | Mat Certs                                   (customer-only)
  // † = customer-only — hidden from suppliers via the isCustomer gate.
  const isCustomer = userType === 'customer';
  // CR-4731 round 2: collapse propose+decision pairs so the Audit History
  // shows one row per change-request lifecycle, not two.
  const collapsedChangelogs = useMemo(() => collapseChangeRequestRows(changelogs), [changelogs]);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left Column - Line Details */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Parties row. CR-4711 (QA 13/08): "Customer field: Missing from the
              top section" — it was rendered for supplier users only, so a
              customer viewing the line never saw which of their own sites the
              order belonged to. It now leads the row for both roles. */}
          {userType === 'supplier' && (
            <ReadOnlyField label="Customer" value={line.customer_name} />
          )}
          {isCustomer && (
            <>
              {/* Customer gets its own row rather than a third column.
                  Squeezing three parties into one row truncated all three —
                  the customer name worst, by 154px, because it is
                  site-qualified ("Acme – Automotive – Germany | Stuttgart").
                  It also reads closer to the design: QA's arrow points at the
                  space above Supplier Group, and Sarah's drawing puts the
                  Customer box there rather than inline with the supplier. */}
              <ReadOnlyField label="Customer" value={line.customer_name} />
              <div className={`grid ${line.supplier_group_name ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                {line.supplier_group_name ? (
                  <ReadOnlyField label="Supplier Group" value={line.supplier_group_name} />
                ) : null}
                <ReadOnlyField label="Supplier" value={line.supplier_name} />
              </div>
            </>
          )}

          {/* Order Date + Buyer ID — customer-only */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Order Date" value={line.order_date ? formatDate(line.order_date) : ''} withCalendarIcon />
              <ReadOnlyField label="Buyer ID" value={line.buyer_id} />
            </div>
          )}

          {/* Part identification: Part Number + Description (+ Drawing Issue customer-only) */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
            <ReadOnlyField label="Part Number(s)" value={line.part_number} primaryLabel />
            {/* CR-7308 rev 2 (Aaron, 18 Sep 2026): the modal carries BOTH, because the
                two are different facts and the modal is where you go to read the line in
                full. "Part Description" is the part master's name for the part (legacy's
                line modal shows this one, from P.PS_Desc). "Order Description" is the
                text typed on THIS line — legacy shows it on the grid only, under the
                bare word "Description", which is how one word came to mean two things. */}
            <ReadOnlyField label="Part Description" value={line.part_description?.trim()} />
            {isCustomer && (
              <ReadOnlyField label="Drawing Issue" value={line.drawing_version} />
            )}
          </div>

          {/* Full width: the order text runs to several lines on a legacy-fed tenant. */}
          <div className="grid grid-cols-1 gap-3">
            <ReadOnlyField label="Order Description" value={line.description?.trim()} />
            {isCustomer && line.po_erp_remarks?.trim() && <ReadOnlyField label="Order Remarks" value={line.po_erp_remarks.trim()} />}
          </div>

          {/* Batch/Serial No + PnR Note No — customer-only */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Batch/Serial No" value={line.batch_serial_no} />
              <ReadOnlyField label="PnR Note No" value={line.packing_note_number} />
            </div>
          )}

          {/* Quantities: Ordered always; the other four customer-only.
              CR-7468: the line said 20 ordered, 2 delivered, 0 outstanding and
              the 18 that explained both sat on the NCR tab, where neither
              figure could see it. Five fields in reading order — ordered, what
              arrived, what was refused, what we kept, what is still to come —
              so the row can be checked by eye:

                  Ordered = Accepted + Rejected + Outstanding

              Holds for every line EXCEPT one: a line carrying a counted NCR with
              no receipt behind it, where the rejected units are also still inside
              Outstanding (R-612). The fix is to the stored outstanding quantity
              and belongs to part 2.

              One vocabulary, every grain (Aaron, 22 Sep 2026): DELIVERED is
              what arrived, REJECTED is what was refused, ACCEPTED is what was
              kept, OUTSTANDING is what is still owed. Before this, one field
              called "Qty Delivered" carried the ACCEPTED number, which is how
              this line could read 20 ordered, 2 delivered and 0 outstanding and
              be telling the truth about neither. The accepted number has not
              moved; it now has its own name, and Delivered has its own field. */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField label="Qty Ordered" value={formatQuantity(line.quantity)} badge={getChangeCount('quantity')} />
            {isCustomer && (
              <>
                <ReadOnlyField label="Qty Delivered" value={formatQuantity(line.delivered_qty)} />
                {/* No colour. These five are one arithmetic statement and a
                    coloured member reads as an alert rather than a term of it
                    (Aaron, 22 Sep). The figure being present at all is the
                    change; it does not need to shout. */}
                <ReadOnlyField label="Qty Rejected" value={formatQuantity(line.rejected_qty)} />
                <ReadOnlyField label="Qty Accepted" value={formatQuantity(line.accepted_qty)} />
                <ReadOnlyField label="Qty Outstanding" value={formatQuantity(line.outstanding_qty)} />
              </>
            )}
          </div>

          <POLineDateFields line={line} formatDate={formatDate} getChangeCount={getChangeCount} />

          {/* Pricing — Unit Price visible to all; Currency + Exchange Rate customer-only */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField
              label="Unit Price"
              value={formatCurrency(line.unit_price)}
              badge={getChangeCount('unit_price')}
            />
            {isCustomer && (
              <>
                <ReadOnlyField label="Currency" value={line.currency} />
                {/* Stored as "1 reporting = X PO-ccy"; shown the way buyers
                    read it ("1 USD = ₹94.30"). The raw rate stays in the title.
                    Customer review 11 Sep (item 27b): the title also names
                    which rate this is — see po_line_money.py for authority. */}
                <div title={line.exchange_rate ? `Stored rate: ${line.exchange_rate} (${EXCHANGE_RATE_SOURCE_LABELS[line.exchange_rate_source || ''] || 'source unknown'})` : undefined}>
                  <ReadOnlyField
                    label="Exchange Rate"
                    value={formatExchangeRateValue(line.exchange_rate, line.currency, line.converted_currency)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Customer-only commercial block — hidden from suppliers entirely */}
          {isCustomer && (
            <>
              {/* Totals row */}
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  label="Total Value"
                  value={line.total_price ? formatCurrency(line.total_price) : ''}
                />
                <ReadOnlyField
                  label="O/S Value"
                  value={line.outstanding_value ? formatCurrency(line.outstanding_value) : ''}
                />
              </div>

              {/* CR-7266: which contract the three prices below came from.
                  Given its own row rather than a fifth column: contract
                  numbers are as long as the site-qualified names that forced
                  the same decision for Customer on Row A, and a fifth cell
                  would have truncated all five. Clicking it opens that
                  contract; with no resolved price band the field shows the
                  same dash as every other empty read-only field and is inert.
                  Blank is the normal case -- only lines the PPV backfill has
                  priced carry a band at all. */}
              <div className="grid grid-cols-1 gap-3">
                <ReadOnlyField
                  label="Contract"
                  value={line.contract_number}
                  onValueClick={
                    line.contract_id
                      ? () => setContractModalId(line.contract_id as number)
                      : undefined
                  }
                  valueTitle="Open this contract"
                />
              </div>

              {/* Contract block (4-col row per design).
                  CR-7275: the order price against its contract -- over is red,
                  exact is green, under is amber, and a line with no contract
                  behind it says so rather than going blank, because a blank
                  box reads as a fault. One verdict drives the price boxes and
                  PPV alike; see contractPriceVariance.ts. */}
              <div className="grid grid-cols-4 gap-3">
                <ReadOnlyField
                  label="Contract Unit Price"
                  value={unitPriceDisplay}
                  valueClassName={unitPriced ? varianceClass : ''}
                />
                <ReadOnlyField
                  label="Contract Lot Price"
                  value={lotPriceDisplay}
                  valueClassName={lotPriced ? varianceClass : ''}
                />
                <ReadOnlyField
                  label="Contract Price"
                  value={onContract
                    ? (line.contract_price ? formatCurrency(line.contract_price) : '')
                    : NO_CONTRACT_TEXT}
                  valueClassName={varianceClass}
                />
                <ReadOnlyField label="PPV" value={line.ppv} valueClassName={varianceClass} />
              </div>

              {/* PDF Attached + Mat Certs row. CR-4711 (QA 13/08): "These
                  should be hyperlinks" — each attached document is now a link
                  that downloads it, in place of the bare count. */}
              <div className="grid grid-cols-2 gap-3">
                <DocumentLinksField
                  label="PDF Attached"
                  documents={line.pdf_attached_documents}
                  count={line.pdf_attached_count}
                />
                <DocumentLinksField
                  label="Mat Certs"
                  documents={line.material_cert_documents}
                  count={line.material_cert_count}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Changelog. Walkthrough batch 3: it fills the column
          rather than stopping at a fixed 500px, which cut it off halfway down
          the customer modal while nothing else uses that space. */}
      <div className="min-h-0">
        <Changelog entries={collapsedChangelogs} fill />
      </div>

      {/* CR-7266: the contract behind the Contract field above. Rendered only
          while open so it fetches on demand rather than on every Detail tab. */}
      {contractModalId !== null && (
        <ContractPriceDetailModal
          contractId={contractModalId}
          isOpen
          onClose={() => setContractModalId(null)}
        />
      )}
    </div>
  );
}
